// AdminPanel.jsx — Site admin panel. Only visible to is_admin === 1 users.
//
// Tabs: 👥 Users | 🔗 Invites | 🪲 Debug Logs | 📧 Email
// This file changes when admin features change (new tabs, new actions).

import React, { useState, useEffect } from 'react';
import { useTheme } from '../constants';
import { Avatar } from './ui';
import { tavern } from '../api';
import DebugLogger from '../debugLogger';

const LEVEL_COLOR = { error: '#EF4444', warn: '#F59E0B', network: '#3B82F6', info: '#22C55E', log: '#6B7280', debug: '#8B5CF6' };

export default function AdminPanel({ onClose, socket }) {
  const C = useTheme();
  const [tab, setTab]             = useState('users');
  const [users, setUsers]         = useState([]);
  const [invites, setInvites]     = useState([]);
  const [logs, setLogs]           = useState([]);
  const [logTotal, setLogTotal]   = useState(0);
  const [logFilter, setLogFilter] = useState('all');
  const [logSearch, setLogSearch] = useState('');
  const [emailCfg, setEmailCfg]   = useState({});
  const [emailPass, setEmailPass] = useState('');
  const [emailMsg, setEmailMsg]   = useState('');
  const [invEmail, setInvEmail]   = useState('');
  const [invNote, setInvNote]     = useState('');
  const [invHours, setInvHours]   = useState(72);
  const [newInvite, setNewInvite] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState('');

  // RTC live log state
  const [rtcLogs, setRtcLogs]         = useState([]);   // { ts, userId, username, msg, type, channel, deviceInfo }
  const [rtcFilter, setRtcFilter]     = useState('');   // filter by username/channel
  const [rtcPaused, setRtcPaused]     = useState(false);
  const rtcEndRef = React.useRef();
  const rtcPausedRef = React.useRef(false);
  rtcPausedRef.current = rtcPaused;

  // Subscribe to live RTC log entries from server
  useEffect(() => {
    if (!socket) return;
    const handler = entry => {
      if (rtcPausedRef.current) return;
      setRtcLogs(prev => {
        const next = [...prev, entry];
        return next.length > 1000 ? next.slice(-1000) : next;
      });
    };
    socket.on('rtc_log_entry', handler);
    return () => socket.off('rtc_log_entry', handler);
  }, [socket]);

  // Auto-scroll RTC log
  useEffect(() => {
    if (tab === 'rtc' && !rtcPaused) rtcEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [rtcLogs, tab, rtcPaused]);

  const flash = (m, err) => { setMsg({ text: m, err: !!err }); setTimeout(() => setMsg(''), 4000); };
  const loadUsers   = () => tavern.adminGetUsers().then(setUsers);
  const loadInvites = () => tavern.get('/admin/invites').then(setInvites);
  const loadLogs    = (filter) => { const f = filter ?? logFilter; tavern.get(`/admin/logs?level=${f}&limit=200`).then(d => { setLogs(d.logs || []); setLogTotal(d.total || 0); }); };
  const loadEmail   = () => tavern.get('/admin/email-config').then(setEmailCfg);

  useEffect(() => { loadUsers(); loadInvites(); loadLogs('all'); loadEmail(); }, []); // eslint-disable-line

  const promote    = async u => { await tavern.post(`/admin/users/${u.id}/promote`, {}); flash(`${u.username} promoted`); loadUsers(); };
  const demote     = async u => { await tavern.post(`/admin/users/${u.id}/demote`, {}); flash(`${u.username} demoted`); loadUsers(); };
  const deleteUser = async u => { if (!window.confirm(`Delete ${u.username}?`)) return; await tavern.delete(`/admin/users/${u.id}`); flash(`${u.username} deleted`); loadUsers(); };

  const createInvite = async () => {
    setBusy(true);
    try { const r = await tavern.post('/admin/invites', { email: invEmail, note: invNote, expires_hours: invHours }); setNewInvite(r); setInvEmail(''); setInvNote(''); loadInvites(); }
    finally { setBusy(false); }
  };
  const deleteInvite = async token => { await tavern.delete(`/admin/invites/${token}`); loadInvites(); };
  const copyLink     = url => { navigator.clipboard.writeText(url); flash('Copied!'); };

  const saveEmail = async () => {
    setBusy(true);
    try { await tavern.put('/admin/email-config', { ...emailCfg, smtp_pass: emailPass || undefined }); flash('Email config saved'); setEmailPass(''); loadEmail(); }
    catch (e) { flash(e.message, true); } finally { setBusy(false); }
  };
  const testEmail = async () => {
    setBusy(true); setEmailMsg('');
    try { await tavern.post('/admin/email-test', { to: emailCfg.from_addr }); setEmailMsg('✅ Test email sent!'); }
    catch (e) { setEmailMsg('❌ ' + e.message); } finally { setBusy(false); }
  };

  const clearLogs  = async () => { if (!window.confirm('Clear all logs?')) return; await tavern.delete('/admin/logs'); loadLogs(); };
  const exportLogs = () => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' })); a.download = `tavern-logs-${Date.now()}.json`; a.click(); };
  const copyLog    = log => { navigator.clipboard.writeText(`[${log.level.toUpperCase()}] ${new Date(log.created_at * 1000).toISOString()}\nUser: ${log.username || 'anon'}\nURL: ${log.url || ''}\n\n${log.message}${log.stack ? '\n\nStack:\n' + log.stack : ''}`); flash('Log copied!'); };

  const filteredLogs = logs.filter(l => !logSearch || l.message?.toLowerCase().includes(logSearch.toLowerCase()) || l.username?.toLowerCase().includes(logSearch.toLowerCase()));
  const tabSt = t => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 700 : 400, background: tab === t ? `${C.accent}22` : 'transparent', color: tab === t ? C.accent : C.muted });
  const inputSt = { background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '7px 12px', fontSize: 13, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const btnSt   = danger => ({ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: danger ? '#EF444422' : `${C.accent}22`, color: danger ? '#EF4444' : C.accent });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, width: 'min(860px,96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, flex: 1 }}>🛡 Admin Panel</div>
          {msg && <div style={{ fontSize: 12, color: msg.err ? '#EF4444' : C.accent, marginRight: 16 }}>{msg.text}</div>}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 22 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {[['users','👥 Users'],['invites','🔗 Invites'],['logs','🪲 Debug Logs'],['email','📧 Email'],['rtc','📡 RTC Logs']].map(([t, label]) => (
            <button key={t} style={tabSt(t)} onClick={() => setTab(t)}>{label}
              {t === 'logs' && logTotal > 0 && <span style={{ marginLeft: 6, background: '#EF444444', color: '#EF4444', fontSize: 10, padding: '1px 6px', borderRadius: 10 }}>{logTotal}</span>}
              {t === 'rtc' && rtcLogs.length > 0 && <span style={{ marginLeft: 6, background: '#00D4AA22', color: '#00D4AA', fontSize: 10, padding: '1px 6px', borderRadius: 10 }}>{rtcLogs.length}</span>}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '18px 24px' }}>

          {/* USERS */}
          {tab === 'users' && (
            <div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>{users.length} registered users</div>
              {users.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                  <Avatar u={u} size={34}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {u.username}
                      {u.is_admin === 1 && <span style={{ background: `${C.accent}22`, color: C.accent, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20 }}>Admin</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>{u.email || u.phone || '—'} · joined {new Date(u.created_at * 1000).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {u.is_admin === 1 ? <button style={btnSt(true)} onClick={() => demote(u)}>Demote</button> : <button style={btnSt(false)} onClick={() => promote(u)}>Make Admin</button>}
                    {u.is_admin !== 1 && <button style={btnSt(true)} onClick={() => deleteUser(u)}>Delete</button>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* INVITES */}
          {tab === 'invites' && (
            <div>
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Create Invite Link</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Email (optional)</div><input style={inputSt} value={invEmail} onChange={e => setInvEmail(e.target.value)} placeholder="user@example.com"/></div>
                  <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Note (optional)</div><input style={inputSt} value={invNote} onChange={e => setInvNote(e.target.value)} placeholder="e.g. Josh's friend"/></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 11, color: C.muted }}>Expires in</div>
                  {[24, 72, 168].map(h => <button key={h} onClick={() => setInvHours(h)} style={{ ...btnSt(false), background: invHours === h ? `${C.accent}33` : 'transparent', border: `1px solid ${invHours === h ? C.accent : C.border}` }}>{h === 24 ? '24h' : h === 72 ? '3 days' : '7 days'}</button>)}
                  <button onClick={createInvite} disabled={busy} style={{ ...btnSt(false), marginLeft: 'auto', padding: '8px 20px' }}>{busy ? 'Creating…' : 'Generate Link'}</button>
                </div>
                {newInvite && (
                  <div style={{ marginTop: 14, background: `${C.accent}11`, border: `1px solid ${C.accent}44`, borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, marginBottom: 6 }}>✅ Invite created!</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ flex: 1, fontSize: 11, color: C.text, wordBreak: 'break-all', background: C.bg, padding: '6px 10px', borderRadius: 6 }}>{newInvite.invite_url}</code>
                      <button style={btnSt(false)} onClick={() => copyLink(newInvite.invite_url)}>Copy</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{invites.length} invites</div>
              {invites.map(inv => (
                <div key={inv.token} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ fontSize: 11, color: C.accent }}>{inv.token.slice(0, 8)}…</code>
                      {inv.used_by ? <span style={{ color: '#22C55E', fontSize: 11 }}>✅ Used</span> : inv.expires_at < Date.now() / 1000 ? <span style={{ color: '#EF4444', fontSize: 11 }}>Expired</span> : <span style={{ color: C.accent, fontSize: 11 }}>Active</span>}
                      {inv.email && <span style={{ color: C.muted, fontSize: 11 }}>→ {inv.email}</span>}
                      {inv.note  && <span style={{ color: C.muted, fontSize: 11 }}>"{inv.note}"</span>}
                    </div>
                    <div style={{ fontSize: 10, color: C.muted }}>Created by {inv.creator} · {new Date(inv.created_at * 1000).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {!inv.used_by && <button style={btnSt(false)} onClick={() => copyLink(`${window.location.origin}/invite/${inv.token}`)}>Copy</button>}
                    <button style={btnSt(true)} onClick={() => deleteInvite(inv.token)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* LOGS */}
          {tab === 'logs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={logSearch} onChange={e => setLogSearch(e.target.value)} placeholder="Search logs…" style={{ ...inputSt, width: 220 }}/>
                {['all','error','warn','network','info','log','debug'].map(f => (
                  <button key={f} onClick={() => { setLogFilter(f); loadLogs(f); }} style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${logFilter === f ? (LEVEL_COLOR[f] || C.accent) : C.border}`, background: logFilter === f ? `${(LEVEL_COLOR[f] || C.accent)}22` : 'transparent', color: logFilter === f ? (LEVEL_COLOR[f] || C.accent) : C.muted, fontSize: 11, cursor: 'pointer' }}>{f}</button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button style={btnSt(false)} onClick={exportLogs}>Export JSON</button>
                  <button style={btnSt(true)} onClick={clearLogs}>Clear All</button>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>{filteredLogs.length} / {logTotal} server + {DebugLogger.getLogs().length} session</div>

              <div style={{ fontSize: 11, fontWeight: 700, color: C.accent }}>🔴 Live Session Logs</div>
              <div style={{ maxHeight: 200, overflow: 'auto', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, padding: 8 }}>
                {DebugLogger.getLogs().filter(l => !logSearch || l.message.toLowerCase().includes(logSearch.toLowerCase())).slice(0, 100).map(l => (
                  <div key={l.id} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: `1px solid ${C.border}22`, fontFamily: 'monospace', fontSize: 11 }}>
                    <span style={{ color: LEVEL_COLOR[l.level] || C.muted, flexShrink: 0, width: 50 }}>[{l.level}]</span>
                    <span style={{ color: C.muted, flexShrink: 0 }}>{l.time?.slice(11, 19)}</span>
                    <span style={{ color: C.text, flex: 1, wordBreak: 'break-word' }}>{l.message.slice(0, 200)}</span>
                    {l.stack && <button onClick={() => setSelectedLog(l)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 10, padding: 0 }}>stack</button>}
                  </div>
                ))}
                {DebugLogger.getLogs().length === 0 && <div style={{ color: C.muted, fontSize: 11, padding: 8 }}>No session logs yet</div>}
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>📦 Server Stored Logs</div>
              <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                {filteredLogs.length === 0 && <div style={{ color: C.muted, fontSize: 12, padding: 16 }}>No logs stored</div>}
                {filteredLogs.map(l => (
                  <div key={l.id} onClick={() => setSelectedLog(l)} style={{ display: 'flex', gap: 8, padding: '8px 10px', borderBottom: `1px solid ${C.border}22`, cursor: 'pointer', fontFamily: 'monospace', fontSize: 11 }}
                    onMouseEnter={e => e.currentTarget.style.background = C.card}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ color: LEVEL_COLOR[l.level] || C.muted, flexShrink: 0, width: 55 }}>[{l.level}]</span>
                    <span style={{ color: C.muted, flexShrink: 0, width: 80 }}>{new Date(l.created_at * 1000).toLocaleTimeString()}</span>
                    <span style={{ color: '#3B82F6', flexShrink: 0, width: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.username || 'anon'}</span>
                    <span style={{ color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.message}</span>
                    {l.stack && <span style={{ color: C.accent, fontSize: 10 }}>⚠</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* EMAIL */}
          {tab === 'email' && (
            <div style={{ maxWidth: 540 }}>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>Configure SMTP for invite emails. Works with <strong style={{ color: C.text }}>Gmail</strong>, <strong style={{ color: C.text }}>Mailgun</strong>, <strong style={{ color: C.text }}>SendGrid</strong>, or any SMTP server.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: C.text }}>Email sending</div>
                <button onClick={() => setEmailCfg(e => ({ ...e, enabled: e.enabled ? 0 : 1 }))} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', background: emailCfg.enabled ? C.accent : C.border, transition: 'background 0.2s' }}>
                  <div style={{ position: 'absolute', top: 2, left: emailCfg.enabled ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: 'white', transition: 'left 0.2s' }}/>
                </button>
                <span style={{ fontSize: 12, color: emailCfg.enabled ? C.accent : C.muted }}>{emailCfg.enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              {[{ label: 'SMTP Host', key: 'host', ph: 'smtp.gmail.com' }, { label: 'SMTP Port', key: 'port', ph: '587' }, { label: 'Username / Email', key: 'smtp_user', ph: 'you@gmail.com' }, { label: 'From Address', key: 'from_addr', ph: 'The Tavern <noreply@yourdomain.com>' }].map(({ label, key, ph }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</div>
                  <input style={inputSt} placeholder={ph} value={emailCfg[key] || ''} onChange={e => setEmailCfg(c => ({ ...c, [key]: e.target.value }))}/>
                </div>
              ))}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Password <span style={{ color: '#F59E0B' }}>(leave blank to keep existing)</span></div>
                <input type="password" style={inputSt} placeholder="••••••••" value={emailPass} onChange={e => setEmailPass(e.target.value)}/>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button onClick={saveEmail} disabled={busy} style={{ ...btnSt(false), padding: '8px 22px' }}>Save Config</button>
                <button onClick={testEmail} disabled={busy || !emailCfg.enabled} style={{ ...btnSt(false), padding: '8px 22px' }}>Send Test Email</button>
                {emailMsg && <span style={{ fontSize: 12, color: emailMsg.startsWith('✅') ? '#22C55E' : '#EF4444' }}>{emailMsg}</span>}
              </div>
            </div>
          )}

          {tab === 'rtc' && (() => {
            // Per-user color palette
            const userColors = ['#00D4AA','#F59E0B','#3B82F6','#A78BFA','#F472B6','#34D399','#FB923C','#60A5FA'];
            const colorMap = {};
            let colorIdx = 0;
            const colorFor = uid => { if (!colorMap[uid]) colorMap[uid] = userColors[colorIdx++ % userColors.length]; return colorMap[uid]; };

            const typeColor = t => ({ ok: '#22C55E', err: '#EF4444', warn: '#F59E0B', info: '#94A3B8' }[t] || '#94A3B8');

            const filtered = rtcFilter
              ? rtcLogs.filter(e => e.username?.toLowerCase().includes(rtcFilter.toLowerCase()) || e.channel?.toLowerCase().includes(rtcFilter.toLowerCase()))
              : rtcLogs;

            const copyAll = () => {
              const lines = filtered.map(e => {
                const ts = new Date(e.ts).toLocaleTimeString('en', { hour12: false });
                return `[${ts}] [${e.username}@${e.channel}] ${e.msg}`;
              }).join('\n');
              const header = filtered.length > 0 && filtered[0].deviceInfo
                ? `# Device: ${JSON.stringify(filtered[0].deviceInfo)}\n\n` : '';
              navigator.clipboard?.writeText(header + lines).then(() => flash('RTC log copied!'));
            };

            const copyUser = (username) => {
              const userEntries = rtcLogs.filter(e => e.username === username);
              if (!userEntries.length) return;
              const di = userEntries.find(e => e.deviceInfo)?.deviceInfo;
              const header = di ? `# ${username} — ${di.isIos ? 'iOS' : di.isSafari ? 'Safari' : 'Desktop'} | ${di.platform} | ${di.screen} | touch=${di.touch}\n# UA: ${di.ua}\n\n` : `# ${username}\n\n`;
              const lines = userEntries.map(e => `[${new Date(e.ts).toLocaleTimeString('en', { hour12: false })}] ${e.msg}`).join('\n');
              navigator.clipboard?.writeText(header + lines).then(() => flash(`Copied ${username}'s log!`));
            };

            // Group unique users seen in this session
            const seenUsers = [...new Map(rtcLogs.map(e => [e.userId, e])).values()];

            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 10 }}>

                {/* Controls */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                  <input style={{ ...inputSt, width: 180 }} placeholder="Filter user/channel..." value={rtcFilter} onChange={e => setRtcFilter(e.target.value)}/>
                  <button style={btnSt(false)} onClick={copyAll}>Copy All</button>
                  <button style={{ ...btnSt(false), color: rtcPaused ? '#F59E0B' : C.muted }} onClick={() => setRtcPaused(p => !p)}>{rtcPaused ? '▶ Resume' : '⏸ Pause'}</button>
                  <button style={btnSt(true)} onClick={() => setRtcLogs([])}>Clear</button>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>{filtered.length} events</span>
                </div>

                {/* Active users bar */}
                {seenUsers.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                    {seenUsers.map(e => {
                      const di = rtcLogs.filter(l => l.userId === e.userId).find(l => l.deviceInfo)?.deviceInfo;
                      const label = di ? `${di.isIos ? '📱iOS' : di.isSafari ? '🧭Safari' : '🖥️'} ${e.username}` : e.username;
                      return (
                        <div key={e.userId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: `${colorFor(e.userId)}22`, border: `1px solid ${colorFor(e.userId)}44`, fontSize: 11 }}>
                          <span style={{ color: colorFor(e.userId), fontWeight: 700 }}>{label}</span>
                          <button onClick={() => copyUser(e.username)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 10, padding: 0 }}>copy</button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Log stream */}
                <div style={{ flex: 1, overflow: 'auto', background: C.bg, borderRadius: 10, padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>
                  {filtered.length === 0
                    ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>No RTC events yet — have someone join a voice channel</div>
                    : filtered.map((e, i) => {
                        const ts = new Date(e.ts).toLocaleTimeString('en', { hour12: false });
                        const userColor = colorFor(e.userId);
                        return (
                          <div key={i} style={{ lineHeight: 1.7, borderBottom: `1px solid ${C.border}`, padding: '1px 0', wordBreak: 'break-all' }}>
                            <span style={{ color: '#374151' }}>[{ts}]</span>{' '}
                            <span style={{ color: userColor, fontWeight: 700 }}>{e.username}</span>
                            <span style={{ color: '#374151' }}>@{e.channel}</span>{' '}
                            <span style={{ color: typeColor(e.type) }}>{e.msg}</span>
                          </div>
                        );
                      })
                  }
                  <div ref={rtcEndRef}/>
                </div>
              </div>
            );
          })()}

        </div>
      </div>

      {/* Log detail modal */}
      {selectedLog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedLog(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, width: 'min(700px,92vw)', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: LEVEL_COLOR[selectedLog.level] || C.accent }}>[{(selectedLog.level || '').toUpperCase()}]</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnSt(false)} onClick={() => copyLog(selectedLog)}>Copy</button>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted }} onClick={() => setSelectedLog(null)}>✕</button>
              </div>
            </div>
            {selectedLog.username && <div style={{ fontSize: 11, color: '#3B82F6', marginBottom: 6 }}>User: {selectedLog.username}</div>}
            {selectedLog.url      && <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>URL: {selectedLog.url}</div>}
            <pre style={{ fontFamily: 'monospace', fontSize: 11, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 12 }}>{selectedLog.message}</pre>
            {selectedLog.stack && <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', marginBottom: 6 }}>Stack Trace:</div>
              <pre style={{ fontFamily: 'monospace', fontSize: 10, color: '#EF4444', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: C.bg, padding: 10, borderRadius: 6 }}>{selectedLog.stack}</pre>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}
